/**
 * MainPanelCommand.js
 * Main Panel (meter_panel) + Feeder ekleme/silme komutları
 * Global undo/redo için scene ve dispatch referanslarını saklar
 *
 * Main Panel eklenirken:
 * 1. Component (meter_panel) oluşturulur
 * 2. Main Feeder (wire/cable) oluşturulur
 * 3. Three.js sahnesine main panel ve wire eklenir
 */

import { addComponent, addFeeder, removeComponent, removeFeeder } from '../../../redux/simpleSupplySlice';
import { deleteWire } from '../../../redux/wireSlice';
import { setCommandType, setOperationData, setCommandMessage } from '../../../redux/operationSlice';
import { COMMANDS } from '../../../utils/commonProperties';
import * as THREE from 'three';
import { createMainPanel } from '../../../factory/symbols/createMainPanel';

class MainPanelCommand {
  /**
   * @param {string} action - 'ADD' veya 'DELETE'
   * @param {object} component - Main Panel component verisi
   * @param {object} feeder - Main Feeder verisi
   * @param {object} options - Ek seçenekler (scene, dispatch, wireId, restoreCallback, restoreVertices)
   */
  constructor(action, component, feeder, options = {}) {
    this.action = action; // 'ADD' veya 'DELETE'
    this.component = component;
    this.feeder = feeder;
    this.options = options;
    this.scene = options.scene; // 🟢 Sahne referansı
    this.dispatch = options.dispatch; // 🟢 Redux dispatch
    this.wireId = options.wireId; // 🟢 Wire ID (wireSlice'tan silmek için)

    // 🆕 Async REDO desteği için restore properties
    this.restoreVertices = options.restoreVertices || [];
    this.restoreSource = options.restoreSource || null;
    this.restoreCallback = options.restoreCallback || null;
    this.sceneData = options; // 🆕 sceneData olarak tüm options'ı sakla
  }

  /**
   * Main Panel + Feeder ekleme işlemini gerçekleştir
   */
  execute() {
    if (!this.scene || !this.dispatch) {
      return;
    }

    if (!this.component) {
      return;
    }

    // 1. Sahneye main panel ekle
    const panelPosition = new THREE.Vector3(
      this.component.position.x,
      this.component.position.y,
      0
    );

    const panelInfo = {
      name: this.component.name || 'Ana Pano',
      installedPower: this.component.metadata?.installedPower || 0,
      phaseCount: this.component.electrical?.phaseCount || 3,
    };

    const panelMesh = createMainPanel(
      this.scene,
      panelPosition,
      { isPreview: false },
      this.component.id,
      panelInfo
    );

    panelMesh.rotation.z = this.component.rotation?.z || 0;

    // Main Panel userData'sına id ve type ekle
    panelMesh.userData = {
      ...this.component,
      ...panelMesh.userData,
      type: 'mainPanel',
      id: this.component.id,
      isElectrical: true
    };

    // ✅ Main Panel ile wire bağlantısı (sürükleme için)
    if (!panelMesh.userData.connectedWires) {
      panelMesh.userData.connectedWires = [];
    }
    panelMesh.userData.connectedWires.push(this.wireId);

    // ✅ Source component (supply/meter_panel) ile wire bağlantısı (sürükleme için)
    const sourceComponentId = this.options.sourceComponentId;
    if (sourceComponentId && this.wireId) {
      let sourceComponent = null;
      this.scene.traverse(obj => {
        if (obj.userData?.id === sourceComponentId) {
          sourceComponent = obj;
        }
      });
      if (sourceComponent) {
        if (!sourceComponent.userData.connectedWires) {
          sourceComponent.userData.connectedWires = [];
        }
        if (!sourceComponent.userData.connectedWires.includes(this.wireId)) {
          sourceComponent.userData.connectedWires.push(this.wireId);
        }
      }
    }

    this.scene.add(panelMesh);

    // 2. Sahneye feeder wire ekle
    if (this.feeder?.vertices?.length > 1) {
      const geometry = new THREE.BufferGeometry().setFromPoints(
        this.feeder.vertices.map((v) => new THREE.Vector3(v.x, v.y, v.z))
      );
      const chosenColor = this.options?.color ?? this.component?.metadata?.color ?? 0x2233ff;
      const material = new THREE.LineBasicMaterial({ color: chosenColor });
      const feederLine = new THREE.Line(geometry, material);
      feederLine.name = 'simpleMainPanelWire';
      feederLine.userData = {
        id: this.wireId,
        type: 'polyline',
        layer: 'simpleMainPanel',
        isSelectable: true,
        componentId: this.component.id,
      };
      this.scene.add(feederLine);
    }

    // 3. Redux'a component ve feeder ekle
    this.dispatch(addComponent(this.component));
    this.dispatch(addFeeder(this.feeder));
  }

  /**
   * Main Panel + Feeder silme işlemini gerçekleştir (undo için)
   */
  undo() {
    if (!this.scene || !this.dispatch) {
      return;
    }

    // ✅ Source component'in connectedWires'ından wire'ı kaldır
    const sourceComponentId = this.options.sourceComponentId;
    if (sourceComponentId && this.wireId) {
      let sourceComponent = null;
      this.scene.traverse(obj => {
        if (obj.userData?.id === sourceComponentId) {
          sourceComponent = obj;
        }
      });
      if (sourceComponent && sourceComponent.userData.connectedWires) {
        const index = sourceComponent.userData.connectedWires.indexOf(this.wireId);
        if (index > -1) {
          sourceComponent.userData.connectedWires.splice(index, 1);
        }
      }
    }

    // 1. Sahnedeki main panel ve wire'ı kaldır
    const toRemove = [];
    this.scene.traverse((obj) => {
      // Main Panel mesh'i bul (userData.id ile)
      if (obj.userData?.id === this.component.id) {
        toRemove.push(obj);
      }
      // Wire'ı bul (userData.componentId veya name ile)
      if (obj.name === 'simpleMainPanelWire' && obj.userData?.componentId === this.component.id) {
        toRemove.push(obj);
      }
      // Alternatif: wireId ile de bulabilir
      if (this.wireId && obj.userData?.id === this.wireId) {
        toRemove.push(obj);
      }
    });

    toRemove.forEach((obj) => {
      // Parent'tan sil
      if (obj.parent) {
        obj.parent.remove(obj);
      } else {
        this.scene.remove(obj);
      }

      // Geometry ve material temizliği
      if (obj.geometry) {
        obj.geometry.dispose();
      }
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach((mat) => mat.dispose());
        } else {
          obj.material.dispose();
        }
      }

      // Group ise children'ları da temizle
      if (obj.children && obj.children.length > 0) {
        obj.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
    });

    // 2. Redux'tan component, feeder ve wire'ı sil
    this.dispatch(removeComponent(this.component.id));
    this.dispatch(removeFeeder(this.feeder.id));

    // Wire slice'tan da sil (eğer wireId varsa)
    if (this.wireId) {
      this.dispatch(deleteWire(this.wireId));
    }

    // 3. 🔁 Yeniden çizim moduna geç (kullanıcı wire çizmeye devam edebilsin)
    if (this.options?.restoreCallback && typeof this.options.restoreCallback === 'function') {
      this.options.restoreCallback(this.options.restoreVertices || []);
    } else {
      console.warn('⚠️ No restoreCallback found or not a function');
    }
  }

}

export default MainPanelCommand;
